import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { CodexToolSummary, ReportingCoverage } from '@ace-tracker/shared';
import { chartAxisTickProps, chartLegendProps, chartTooltipProps, chartXAxisTickProps } from '../chartTheme';
import { CODEX_METRIC_INFO } from '../metricInfo';
import { RepositoryBreakdownTable } from './RepositoryBreakdownTable';
import { ChartCard } from './ChartCard';
import { CodexCacheEfficiencyChart } from './CodexCacheEfficiencyChart';
import { CodexDrillDownTable } from './CodexDrillDownTable';
import { CodexRateLimitGauge } from './CodexRateLimitGauge';
import { ContextWeightChart } from './ContextWeightChart';
import { EffortMixChart } from './EffortMixChart';
import { EfficiencyGauge } from './EfficiencyGauge';
import { KpiCard } from './KpiCard';
import { PricingBasisNote } from './PricingBasisNote';
import { ModelCostTable } from './ModelCostTable';

/** The Codex-specific body of the dashboard — the counterpart to CopilotDashboardBody/ClaudeCodeDashboardBody, for CodexToolSummary. */
export function CodexDashboardBody({
  toolData,
  developersCount,
  coverage,
  formatCurrency,
  SectionHeading,
}: {
  toolData: CodexToolSummary;
  developersCount: number;
  coverage: ReportingCoverage;
  formatCurrency: (value: number, currency: string) => string;
  SectionHeading: (props: { title: string; hint?: string; info?: string }) => JSX.Element;
}) {
  const grouped = groupCodexTimeSeries(toolData.timeSeries);

  return (
    <>
      <section>
        <SectionHeading title="Headline metrics" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <KpiCard
            label="Indicative token cost"
            value={formatCurrency(toolData.indicativeCost.totalCost, toolData.indicativeCost.currency)}
            meta={
              toolData.indicativeCost.unpricedModels.length > 0
                ? `Per-model pricing; ${toolData.indicativeCost.unpricedModels.length} model(s) at fallback rate`
                : 'Priced per model from Settings'
            }
            flag="Estimate"
            info={CODEX_METRIC_INFO.indicativeCost}
            hero
            accentClassName="bg-primary-blue"
          />
          <KpiCard
            label="Sessions"
            value={toolData.activity.sessions.toLocaleString()}
            meta="Distinct root Codex sessions in range"
            info={CODEX_METRIC_INFO.sessions}
            accentClassName="bg-sky-blue"
          />
          <KpiCard
            label="Active minutes"
            value={toolData.activity.activeMinutes.toLocaleString()}
            meta="Summed per-session span, all sessions"
            info={CODEX_METRIC_INFO.activeMinutes}
            accentClassName="bg-sky-blue"
          />
          <KpiCard
            label="Active developers"
            value={String(developersCount)}
            meta={`${coverage.reportingDevices} of ${coverage.expectedDevices} devices reporting`}
            info={CODEX_METRIC_INFO.activeDevelopers}
            accentClassName="bg-sky-blue"
          />
          <KpiCard
            label="Tokens processed"
            value={(toolData.contextWeight.inputTokens + toolData.contextWeight.outputTokens).toLocaleString()}
            meta={`${toolData.contextWeight.ratio > 0 ? `${toolData.contextWeight.ratio}:1` : '—'} input:output ratio`}
            info={CODEX_METRIC_INFO.tokensProcessed}
            accentClassName="bg-info"
          />
          <KpiCard
            label="Subagent delegation"
            value={String(Math.round(toolData.activity.subagentShare * 100))}
            unit="%"
            meta="Share of requests from a spawned subagent thread"
            info={CODEX_METRIC_INFO.subagentShare}
            accentClassName="bg-info"
          />
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <EfficiencyGauge
            label="Cache hit ratio"
            value={toolData.cacheEfficiency.cacheHitRatio * 100}
            valueLabel={`${Math.round(toolData.cacheEfficiency.cacheHitRatio * 100)}%`}
            meta="Share of input tokens served from cache"
            info={CODEX_METRIC_INFO.cacheEfficiency}
            size="sm"
          />
          <EfficiencyGauge
            label="Reporting coverage"
            value={coverage.coveragePercent}
            valueLabel={`${coverage.coveragePercent}%`}
            meta={`${coverage.reportingDevices} of ${coverage.expectedDevices} devices, last 48h`}
            info={CODEX_METRIC_INFO.reportingCoverage}
            size="sm"
          />
        </div>
        <div className="mt-3">
          <PricingBasisNote
            basis={{
              source: toolData.indicativeCost.source,
              reviewedAt: toolData.indicativeCost.pricesReviewedAt,
              stale: toolData.indicativeCost.pricesStale,
              fallbackCost: toolData.indicativeCost.byModel.filter((m) => m.pricedAs === null).reduce((sum, m) => sum + m.cost, 0),
            }}
            totalCost={toolData.indicativeCost.totalCost}
            unpricedModels={toolData.indicativeCost.unpricedModels}
          />
        </div>
      </section>

      <section>
        <ModelCostTable
          cost={toolData.indicativeCost}
          formatCurrency={formatCurrency}
          inputLabel="Billable input"
          cacheReadLabel="Cached input"
          showCacheWrite={false}
          info={CODEX_METRIC_INFO.indicativeCost}
        />
      </section>

      <section>
        <SectionHeading title="Trends & breakdown" />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          <ChartCard
            title="Engagement trend"
            caption="Requests and sessions per day, corrected to max() per device"
            info={CODEX_METRIC_INFO.engagementTrend}
            height="h-96"
            className="lg:col-span-7"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={grouped}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
                <XAxis dataKey="date" {...chartXAxisTickProps} />
                <YAxis {...chartAxisTickProps} />
                <Tooltip {...chartTooltipProps} />
                <Legend {...chartLegendProps} />
                <Bar dataKey="requests" fill="#0058AB" name="Requests" />
                <Bar dataKey="sessions" fill="#06D6A0" name="Sessions" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Quota headroom"
            caption="5-hour and weekly Codex quota consumption"
            info={CODEX_METRIC_INFO.rateLimit}
            className="lg:col-span-5"
          >
            <CodexRateLimitGauge rateLimit={toolData.rateLimit} />
          </ChartCard>

          <ChartCard
            title="Cache efficiency"
            caption="Billable (fresh) vs. cached input tokens"
            info={CODEX_METRIC_INFO.cacheEfficiency}
            className="lg:col-span-5"
          >
            <CodexCacheEfficiencyChart cacheEfficiency={toolData.cacheEfficiency} />
          </ChartCard>

          <ChartCard
            title="Context weight"
            caption={`Input vs output tokens${toolData.contextWeight.ratio > 0 ? ` — ${toolData.contextWeight.ratio}:1 means context, not generation, drives spend` : ''}`}
            info={CODEX_METRIC_INFO.contextWeight}
            className="lg:col-span-4"
          >
            <ContextWeightChart contextWeight={toolData.contextWeight} />
          </ChartCard>

          <ChartCard
            title="Reasoning-effort mix"
            caption="Token volume by low/medium/high reasoning effort"
            info={CODEX_METRIC_INFO.byEffort}
            className="lg:col-span-3"
          >
            <EffortMixChart byEffort={toolData.byEffort} />
          </ChartCard>

          <ChartCard
            title="Usage by model"
            caption="De-duplicated turn events per model"
            info={CODEX_METRIC_INFO.usageByModel}
            className="lg:col-span-6"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={toolData.byModel}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
                <XAxis dataKey="model" {...chartXAxisTickProps} />
                <YAxis {...chartAxisTickProps} />
                <Tooltip {...chartTooltipProps} />
                <Bar dataKey="events" fill="#0058AB" name="Events" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title="Requests by surface"
            caption="Codex Desktop, CLI, or IDE extension"
            info={CODEX_METRIC_INFO.bySurface}
            className="lg:col-span-6"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={toolData.bySurface}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
                <XAxis dataKey="surface" {...chartXAxisTickProps} />
                <YAxis {...chartAxisTickProps} />
                <Tooltip {...chartTooltipProps} />
                <Legend {...chartLegendProps} />
                <Bar dataKey="activeDevices" fill="#0058AB" name="Active devices" />
                <Bar dataKey="totalEvents" fill="#06D6A0" name="Total events" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </section>

      <section>
        <CodexDrillDownTable breakdown={toolData.modelDeveloperBreakdown} info={CODEX_METRIC_INFO.drillDown} />
      </section>

      <section>
        <RepositoryBreakdownTable byBranch={toolData.byBranch} info={CODEX_METRIC_INFO.byBranch} />
      </section>
    </>
  );
}

function groupCodexTimeSeries(timeSeries: { date: string; metricName: string; value: number }[]) {
  const byDate = new Map<string, { date: string; requests: number; sessions: number }>();
  for (const point of timeSeries) {
    const row = byDate.get(point.date) ?? { date: point.date, requests: 0, sessions: 0 };
    if (point.metricName === 'codex.requests') row.requests = point.value;
    if (point.metricName === 'codex.sessions') row.sessions = point.value;
    byDate.set(point.date, row);
  }
  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
}
